import { useEffect, useState } from 'react'
import {
  useAddComment,
  useDeleteComment,
  useDeleteTask,
  useDirectory,
  useProjects,
  useTask,
  useUpdateTask,
} from '../api/hooks'
import type { TaskPriority, TaskStatus } from '../api/types'
import { formatDateTime, relativeTime, toDateInputValue, fromDateInputValue } from '../lib/dates'
import { Avatar } from './TaskRow'
import { Button, ErrorNotice, Loading, PRIORITY_LABEL, TASK_STATUS_LABEL, cx } from './ui'

const STATUSES: TaskStatus[] = ['TODO', 'IN_PROGRESS', 'BLOCKED', 'REVIEW', 'DONE']
const PRIORITIES: TaskPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'URGENT']

/**
 * Slide-over panel for a single task. Opens over whatever view you were on, so
 * you never lose your place in a list.
 */
export function TaskDrawer({ taskId, onClose }: { taskId: string; onClose: () => void }) {
  const { data, isLoading, error } = useTask(taskId)
  const updateTask = useUpdateTask()
  const deleteTask = useDeleteTask()
  const addComment = useAddComment(taskId)
  const deleteComment = useDeleteComment(taskId)
  const { data: people } = useDirectory()
  const { data: projects } = useProjects()

  const [comment, setComment] = useState('')
  const [editingTitle, setEditingTitle] = useState(false)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [descriptionDirty, setDescriptionDirty] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const task = data?.task
  const canWrite = data?.permissions.canWrite ?? false
  const canDelete = data?.permissions.canDelete ?? false

  // Reset local edit state whenever a different task is opened.
  useEffect(() => {
    setComment('')
    setEditingTitle(false)
    setDescriptionDirty(false)
    setConfirmingDelete(false)
  }, [taskId])

  useEffect(() => {
    if (task) {
      setTitle(task.title)
      if (!descriptionDirty) setDescription(task.description ?? '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id, task?.title, task?.description])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  type TaskPatch = Partial<{
    title: string
    description: string | null
    status: TaskStatus
    priority: TaskPriority
    assigneeId: string | null
    dueDate: string | null
    projectId: string | null
  }>

  function patch(changes: TaskPatch) {
    updateTask.mutate({ id: taskId, ...changes })
  }

  async function handleDelete() {
    await deleteTask.mutateAsync(taskId)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <button
        type="button"
        aria-label="Close panel"
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/30 backdrop-blur-[1px]"
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Task details"
        className="relative flex h-full w-full max-w-xl flex-col overflow-y-auto border-l border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900"
      >
        <header className="sticky top-0 flex items-start gap-3 border-b border-slate-200 bg-white/95 px-5 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
          <div className="min-w-0 flex-1">
            {task && (
              <p className="mb-1 truncate text-xs text-slate-500 dark:text-slate-400">
                {task.company?.name ?? 'No company'}
                {task.project ? ` › ${task.project.name}` : ''}
              </p>
            )}

            {editingTitle && canWrite ? (
              <div className="flex gap-2">
                <input
                  className="field"
                  value={title}
                  autoFocus
                  onChange={(e) => setTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && title.trim()) {
                      patch({ title: title.trim() })
                      setEditingTitle(false)
                    }
                  }}
                />
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    if (title.trim()) patch({ title: title.trim() })
                    setEditingTitle(false)
                  }}
                >
                  Save
                </Button>
              </div>
            ) : (
              <h2
                className={cx(
                  'text-lg font-semibold tracking-tight',
                  canWrite && 'cursor-text rounded px-1 -mx-1 hover:bg-slate-100 dark:hover:bg-slate-800',
                )}
                onClick={() => canWrite && setEditingTitle(true)}
                title={canWrite ? 'Click to rename' : undefined}
              >
                {task?.title ?? 'Task'}
              </h2>
            )}
          </div>

          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
              <path d="M6.3 5 5 6.3 8.6 10 5 13.7 6.3 15 10 11.4l3.7 3.6 1.3-1.3-3.6-3.7 3.6-3.7L13.7 5 10 8.6 6.3 5Z" />
            </svg>
          </Button>
        </header>

        <div className="flex-1 px-5 py-4">
          {isLoading && <Loading />}
          {error && <ErrorNotice error={error} />}

          {task && (
            <>
              {!canWrite && (
                <p className="mb-4 rounded-lg bg-slate-100 px-3 py-2 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                  You have read-only access to this task. You can still add comments.
                </p>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="label" htmlFor="task-status">
                    Status
                  </label>
                  <select
                    id="task-status"
                    className="field"
                    value={task.status}
                    disabled={!canWrite}
                    onChange={(e) => patch({ status: e.target.value as TaskStatus })}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {TASK_STATUS_LABEL[s]}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="label" htmlFor="task-priority">
                    Priority
                  </label>
                  <select
                    id="task-priority"
                    className="field"
                    value={task.priority}
                    disabled={!canWrite}
                    onChange={(e) => patch({ priority: e.target.value as TaskPriority })}
                  >
                    {PRIORITIES.map((p) => (
                      <option key={p} value={p}>
                        {PRIORITY_LABEL[p]}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="label" htmlFor="task-assignee">
                    Assignee
                  </label>
                  <select
                    id="task-assignee"
                    className="field"
                    value={task.assignee?.id ?? ''}
                    disabled={!canWrite}
                    onChange={(e) => patch({ assigneeId: e.target.value || null })}
                  >
                    <option value="">Unassigned</option>
                    {people?.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="label" htmlFor="task-due">
                    Due date
                  </label>
                  <input
                    id="task-due"
                    type="date"
                    className="field"
                    value={toDateInputValue(task.dueDate)}
                    disabled={!canWrite}
                    onChange={(e) => patch({ dueDate: fromDateInputValue(e.target.value) })}
                  />
                </div>

                {projects && projects.length > 0 && (
                  <div className="sm:col-span-2">
                    <label className="label" htmlFor="task-project">
                      Project
                    </label>
                    <select
                      id="task-project"
                      className="field"
                      value={task.project?.id ?? ''}
                      disabled={!canWrite}
                      onChange={(e) => {
                        if (e.target.value) patch({ projectId: e.target.value })
                      }}
                    >
                      {!task.project && <option value="">No project (loose task)</option>}
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.company.shortName ?? p.company.name} › {p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="mt-5">
                <label className="label" htmlFor="task-description">
                  Notes
                </label>
                <textarea
                  id="task-description"
                  className="field min-h-24"
                  placeholder={canWrite ? 'Context, links, next steps…' : 'No notes'}
                  value={description}
                  disabled={!canWrite}
                  onChange={(e) => {
                    setDescription(e.target.value)
                    setDescriptionDirty(true)
                  }}
                />
                {descriptionDirty && (
                  <div className="mt-2 flex gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      loading={updateTask.isPending}
                      onClick={() => {
                        patch({ description: description })
                        setDescriptionDirty(false)
                      }}
                    >
                      Save notes
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        setDescription(task.description ?? '')
                        setDescriptionDirty(false)
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>

              {updateTask.error && (
                <div className="mt-3">
                  <ErrorNotice error={updateTask.error} />
                </div>
              )}

              <dl className="mt-5 grid gap-1 border-t border-slate-200 pt-4 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
                <div className="flex gap-2">
                  <dt>Created by</dt>
                  <dd className="font-medium text-slate-700 dark:text-slate-300">
                    {task.createdBy.name}
                  </dd>
                  <dd>· {formatDateTime(task.createdAt)}</dd>
                </div>
                {task.completedAt && (
                  <div className="flex gap-2">
                    <dt>Completed</dt>
                    <dd>{formatDateTime(task.completedAt)}</dd>
                  </div>
                )}
              </dl>

              {/* ---------------------------------------------------------- */}
              <section className="mt-6">
                <h3 className="mb-3 text-sm font-semibold">
                  Comments{' '}
                  {data.comments.length > 0 && (
                    <span className="text-slate-400">({data.comments.length})</span>
                  )}
                </h3>

                <div className="space-y-3">
                  {data.comments.map((c) => (
                    <div key={c.id} className="flex gap-2.5">
                      <Avatar name={c.author.name} />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          <span className="font-medium text-slate-700 dark:text-slate-300">
                            {c.author.name}
                          </span>{' '}
                          · {relativeTime(c.createdAt)}
                          {c.mine && (
                            <button
                              type="button"
                              onClick={() => deleteComment.mutate(c.id)}
                              className="ml-2 text-red-600 hover:underline dark:text-red-400"
                            >
                              delete
                            </button>
                          )}
                        </p>
                        <p className="mt-0.5 text-sm whitespace-pre-wrap">{c.body}</p>
                      </div>
                    </div>
                  ))}
                  {data.comments.length === 0 && (
                    <p className="text-sm text-slate-500 dark:text-slate-400">No comments yet.</p>
                  )}
                </div>

                <form
                  className="mt-3 flex gap-2"
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (!comment.trim()) return
                    addComment.mutate(comment.trim(), { onSuccess: () => setComment('') })
                  }}
                >
                  <input
                    className="field"
                    placeholder="Add a comment…"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                  <Button
                    type="submit"
                    variant="primary"
                    loading={addComment.isPending}
                    disabled={!comment.trim()}
                  >
                    Post
                  </Button>
                </form>
                {addComment.error && (
                  <div className="mt-2">
                    <ErrorNotice error={addComment.error} />
                  </div>
                )}
              </section>

              {canDelete && (
                <footer className="mt-8 border-t border-slate-200 pt-4 dark:border-slate-800">
                  {confirmingDelete ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        Delete this task permanently?
                      </span>
                      <Button variant="danger" size="sm" loading={deleteTask.isPending} onClick={handleDelete}>
                        Yes, delete
                      </Button>
                      <Button size="sm" onClick={() => setConfirmingDelete(false)}>
                        Keep it
                      </Button>
                    </div>
                  ) : (
                    <Button variant="danger" size="sm" onClick={() => setConfirmingDelete(true)}>
                      Delete task
                    </Button>
                  )}
                  {deleteTask.error && (
                    <div className="mt-2">
                      <ErrorNotice error={deleteTask.error} />
                    </div>
                  )}
                </footer>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  )
}
